/**
 * Font Configuration
 *
 * Preload rules:
 * - satoshi (Regular 400, Bold 700): preloaded — applied to <body>, first paint
 * - fredoka: preloaded — hero heading, nav wordmark (first paint)
 * - uiFont (Satoshi Variable): preloaded on product routes only (dashboard/admin/share
 *   layouts import it), one latin+latin-ext subset woff2 (~36 KB).
 * - All others: preload: false — loaded on demand after paint
 *
 * Product UI (DESIGN.md §3) uses ONE family, `uiFont`, applied via the
 * `font-ui` utility that `appFontVariables` carries. Cal Sans stays exported
 * for marketing/auth pages only.
 */

import localFont from 'next/font/local';

// JetBrains Mono — robotic terminal/agent font for the CMO (Moxxie) chat.
// Lazy (preload:false); only the chat applies it.
// Local file avoids fonts.gstatic.com fetch at build time (ETIMEDOUT on prod VPS).
export const jetbrainsMono = localFont({
  src: [
    {
      path: '../../public/fonts/jetbrains-mono/JetBrainsMono-latin-ext.var.woff2',
      weight: '100 800',
      style: 'normal',
    },
  ],
  variable: '--font-jetbrains-mono',
  display: 'swap',
  preload: false,
});

// Fredoka - Title and heading font
// Bold, rounded font with playful and modern aesthetic
export const fredoka = localFont({
  src: [
    {
      path: '../../public/fonts/fredoka/Fredoka[wdth,wght].ttf',
      weight: '300 700',
      style: 'normal',
    },
  ],
  variable: '--font-fredoka',
  display: 'swap',
  preload: true,
  fallback: ['-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'sans-serif'],
});

// Cal Sans - Navigation menu and CTA font
// Modern, geometric sans-serif
export const calSans = localFont({
  src: [
    {
      path: '../../public/fonts/cal-sans/CalSans-Regular.ttf',
      weight: '400',
      style: 'normal',
    },
  ],
  variable: '--font-cal-sans',
  display: 'swap',
  preload: false,
  fallback: ['-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'sans-serif'],
});

// Satoshi - Primary body text font applied to <body>. Same subset variable
// woff2 the product uiFont uses (next/font hashes by content, so the two
// exports share ONE ~36 KB asset) — replaced three ~50 KB OTFs per page load.
export const satoshi = localFont({
  src: [
    {
      path: '../../public/fonts/satoshi-variable/Satoshi-latin-ext.var.woff2',
      weight: '300 900',
      style: 'normal',
    },
  ],
  variable: '--font-satoshi',
  display: 'swap',
  preload: true,
  adjustFontFallback: 'Arial',
  fallback: ['-apple-system', 'BlinkMacSystemFont', 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'sans-serif'],
});

// Scope font variables so the marketing shell only carries what the homepage needs.
export const marketingFontVariables = `${fredoka.variable} ${satoshi.variable} ${jetbrainsMono.variable}`;

// Satoshi Variable — the single product UI family (DESIGN.md §3). Variable
// weight axis kept (300–900, a true 600/semibold), latin + latin-ext subset
// built with pyftsubset from the Fontshare release (LICENSE next to the
// file). `adjustFontFallback` size-matches the fallback so the swap does not
// shift layout. This is a separate resource from the marketing `satoshi`
// export above (discrete OTF weights, no subsetting) — different routes,
// different weight-matching needs.
export const uiFont = localFont({
  src: [
    {
      path: '../../public/fonts/satoshi-variable/Satoshi-latin-ext.var.woff2',
      weight: '300 900',
      style: 'normal',
    },
  ],
  variable: '--font-ui-family',
  display: 'swap',
  preload: true,
  adjustFontFallback: 'Arial',
  fallback: ['ui-sans-serif', 'system-ui', '-apple-system', 'Segoe UI', 'Roboto', 'sans-serif'],
});

// Product root class list: the Satoshi variable plus the `font-ui` utility that
// sets font-family for everything beneath (see @theme inline in globals.css).
export const appFontVariables = `${uiFont.variable} ${jetbrainsMono.variable} font-ui`;
